import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, CreditCard, X, Lightbulb, Truck, MapPin, ZoomIn, Gift } from 'lucide-react';
import { useAuth, API } from '../App';
import { categoryLabel, t } from '../i18n';
import Header from '../components/Header';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const Shop = () => {
  const { user, features } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [suggestionEmail, setSuggestionEmail] = useState('');
  
  // Shipping
  const [shippingAddress, setShippingAddress] = useState({
    street: '', city: '', postal_code: '', country: 'France'
  });
  const [shippingCost, setShippingCost] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [useSavedAddress, setUseSavedAddress] = useState(false);
  const [addressValid, setAddressValid] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);

  // Referral / promo code
  const [referralInput, setReferralInput] = useState('');
  const [referralApplied, setReferralApplied] = useState(null); // { code, discount_percent, influencer_name }
  const [referralChecking, setReferralChecking] = useState(false);

  const applyReferralCode = async () => {
    const code = (referralInput || '').trim().toUpperCase();
    if (!code) return;
    setReferralChecking(true);
    try {
      const res = await axios.post(`${API}/referrals/validate`, { code });
      setReferralApplied(res.data);
      toast.success(`Code ${res.data.code} applique : -${res.data.discount_percent}%`);
    } catch (err) {
      setReferralApplied(null);
      toast.error(err?.response?.data?.detail || 'Code invalide');
    } finally {
      setReferralChecking(false);
    }
  };

  const removeReferralCode = () => {
    setReferralApplied(null);
    setReferralInput('');
  };

  useEffect(() => {
    fetchProducts();
    // Load cart from localStorage
    const savedCart = localStorage.getItem('tagora_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    // Load saved addresses if logged in
    fetchSavedAddresses();
  }, []);

  useEffect(() => {
    // Save cart to localStorage
    localStorage.setItem('tagora_cart', JSON.stringify(cart));
  }, [cart]);

  // Validate address whenever it changes
  useEffect(() => {
    const { street, city, postal_code, country } = shippingAddress;
    setAddressValid(
      street.trim().length >= 3 &&
      city.trim().length >= 2 &&
      postal_code.trim().length >= 4 &&
      country.trim().length >= 2
    );
  }, [shippingAddress]);

  const fetchSavedAddresses = async () => {
    try {
      const res = await axios.get(`${API}/user/addresses`, { withCredentials: true });
      if (res.data && res.data.length > 0) {
        setSavedAddresses(res.data);
      }
    } catch { /* not logged in or no addresses */ }
  };

  const selectSavedAddress = (addr) => {
    setShippingAddress({
      street: addr.street || '',
      city: addr.city || '',
      postal_code: addr.postal_code || '',
      country: addr.country || 'France'
    });
    setUseSavedAddress(true);
    setShippingCost(null);
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error(t('actions.search') + '...');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.product_id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success(`${product.name} - ${t('shop.addToCart')}`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => {
      const updated = prev.map(item =>
        item.product_id === productId
          ? { ...item, quantity: Math.max(0, item.quantity + delta) }
          : item
      ).filter(item => item.quantity > 0);
      return updated;
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const calculateShipping = async () => {
    if (!addressValid) {
      toast.error(t('shop.shippingAddress'));
      return;
    }
    try {
      const response = await axios.post(`${API}/shipping/calculate`, {
        postal_code: shippingAddress.postal_code,
        country: shippingAddress.country,
        cart_total: cartTotal
      });
      setShippingCost(response.data);
    } catch (error) {
      toast.error(t('shop.calculateShipping'));
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error(t('nav.login'));
      return;
    }

    if (cart.length === 0) {
      toast.error(t('shop.emptyCart'));
      return;
    }

    if (!addressValid) {
      toast.error(t('shop.shippingAddress'));
      return;
    }

    if (!shippingCost) {
      toast.error(t('shop.calculateShipping'));
      return;
    }

    setCheckingOut(true);
    try {
      const response = await axios.post(
        `${API}/checkout/stripe`,
        {
          cart_items: cart.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          shipping_address: shippingAddress,
          origin_url: window.location.origin,
          referral_code: referralApplied?.code || null,
        },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Erreur lors de la creation de la commande');
      setCheckingOut(false);
    }
  };

  const submitSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestion.trim()) return;
    try {
      await axios.post(`${API}/products/suggest`, {
        suggestion,
        email: suggestionEmail || null
      });
      toast.success('Merci pour votre suggestion!');
      setShowSuggestionDialog(false);
      setSuggestion('');
      setSuggestionEmail('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const categoryLabels = {
    stickers: 'Stickers',
    keychains: 'Porte-clés',
    pet_tags: 'Médailles Animal',
    bracelets: 'Bracelets',
    car_stickers: 'Auto',
    luggage: 'Bagages',
    doudou: 'Doudou'
  };

  if (features?.shop_enabled === false) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="px-6 sm:px-8 lg:px-12 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-charcoal mb-2">{t('shop.tag')}</p>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-charcoal font-heading">
                {t('shop.title')}
              </h1>
              <p className="text-charcoal/70 mt-2">
                {t('shop.subtitle')}
              </p>
            </div>
            <div className="flex gap-3">
              {features?.suggestions_enabled !== false && (
              <Dialog open={showSuggestionDialog} onOpenChange={setShowSuggestionDialog}>
                <DialogTrigger asChild>
                  <button data-testid="suggest-product-btn" className="btn-outline flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" strokeWidth={2.5} />
                    {t('shop.suggest')}
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold font-heading">{t('shop.suggest')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitSuggestion} className="space-y-4 mt-4">
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                        Votre idée *
                      </label>
                      <textarea
                        value={suggestion}
                        onChange={(e) => setSuggestion(e.target.value)}
                        data-testid="suggestion-input"
                        className="input-brutal w-full h-32 resize-none"
                        placeholder={t('shop.suggestPlaceholder')}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                        Email (optionnel)
                      </label>
                      <input
                        type="email"
                        value={suggestionEmail}
                        onChange={(e) => setSuggestionEmail(e.target.value)}
                        className="input-brutal w-full"
                        placeholder={t('shop.suggestEmail')}
                      />
                    </div>
                    <button type="submit" className="w-full btn-primary">
                      Envoyer ma suggestion
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
              )}
              <button
                onClick={() => setShowCart(true)}
                data-testid="cart-btn"
                className="btn-primary relative flex items-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" strokeWidth={2.5} />
                {t('shop.cart')}
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-charcoal text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Free shipping banner */}
          <div className="bg-secondary border-2 border-charcoal rounded-xl p-4 mb-8 flex items-center gap-3">
            <Truck className="w-6 h-6 text-charcoal" strokeWidth={2.5} />
            <p className="font-bold text-charcoal">{t('shop.freeShipping')}</p>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div
                  key={product.product_id}
                  data-testid={`product-card-${product.product_id}`}
                  className="bg-white border-2 border-charcoal rounded-2xl overflow-hidden shadow-brutal hover:shadow-brutal-xl hover:-translate-y-1 transition-all flex flex-col"
                >
                  <div className="relative group cursor-pointer" onClick={() => setZoomedImage(product.image_url)}>
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-48 object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-colors flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                    </div>
                    <span className="absolute top-3 left-3 bg-accent text-charcoal text-xs font-bold px-3 py-1 rounded-full border-2 border-charcoal">
                      {categoryLabel(product.category) || product.category}
                    </span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-bold text-lg text-charcoal font-heading mb-2">
                      {product.name}
                    </h3>
                    <p className="text-sm text-charcoal/70 mb-4 flex-1">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-charcoal font-heading">
                        {product.price.toFixed(2)}€
                      </span>
                      <button
                        onClick={() => addToCart(product)}
                        data-testid={`add-to-cart-${product.product_id}`}
                        className="btn-secondary text-sm px-4 py-2"
                      >
                        <Plus className="w-4 h-4 inline mr-1" strokeWidth={2.5} />
                        {t('shop.addToCart')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-charcoal/50"
            onClick={() => setShowCart(false)}
          ></div>
          <div className="relative w-full max-w-md bg-background border-l-2 border-charcoal h-full overflow-y-auto">
            <div className="sticky top-0 bg-background border-b-2 border-charcoal p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-charcoal font-heading">
                {t('shop.cart')} ({cartCount})
              </h2>
              <button
                onClick={() => setShowCart(false)}
                data-testid="close-cart-btn"
                className="p-2 rounded-full border-2 border-charcoal hover:bg-surface-alt transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 text-charcoal/20 mx-auto mb-4" />
                  <p className="text-charcoal/70">Votre panier est vide</p>
                  <button
                    onClick={() => setShowCart(false)}
                    className="btn-outline mt-4"
                  >
                    Continuer mes achats
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item) => (
                      <div
                        key={item.product_id}
                        data-testid={`cart-item-${item.product_id}`}
                        className="bg-white border-2 border-charcoal rounded-xl p-4 flex gap-4"
                      >
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg border-2 border-charcoal"
                        />
                        <div className="flex-1">
                          <h4 className="font-bold text-charcoal text-sm">{item.name}</h4>
                          <p className="text-primary font-bold">{item.price.toFixed(2)}€</p>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => updateQuantity(item.product_id, -1)}
                              data-testid={`decrease-${item.product_id}`}
                              className="w-8 h-8 rounded-full border-2 border-charcoal flex items-center justify-center hover:bg-surface-alt"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-bold">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.product_id, 1)}
                              data-testid={`increase-${item.product_id}`}
                              className="w-8 h-8 rounded-full border-2 border-charcoal flex items-center justify-center hover:bg-surface-alt"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeFromCart(item.product_id)}
                              data-testid={`remove-${item.product_id}`}
                              className="ml-auto text-danger hover:text-danger/80"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Shipping Address */}
                  <div className="bg-surface-alt border-2 border-charcoal/20 rounded-xl p-4 mb-4">
                    <p className="font-bold text-charcoal text-sm mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Adresse de livraison *
                    </p>

                    {/* Saved addresses */}
                    {savedAddresses.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-charcoal/60 mb-2">Adresses enregistrees :</p>
                        <div className="space-y-1.5">
                          {savedAddresses.map((addr, i) => (
                            <button
                              key={i}
                              onClick={() => selectSavedAddress(addr)}
                              data-testid={`saved-address-${i}`}
                              className={`w-full text-left text-xs px-3 py-2 rounded-lg border-2 transition-all ${
                                useSavedAddress && shippingAddress.street === addr.street
                                  ? 'border-primary bg-primary/10'
                                  : 'border-charcoal/10 hover:border-charcoal/30 bg-white'
                              }`}
                            >
                              <span className="font-bold">{addr.street}</span>, {addr.postal_code} {addr.city}
                              {addr.is_default && <span className="ml-1 text-primary font-bold">(par defaut)</span>}
                            </button>
                          ))}
                        </div>
                        <div className="border-t border-charcoal/10 mt-3 pt-2">
                          <p className="text-xs text-charcoal/50 mb-2">Ou saisir une nouvelle adresse :</p>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <input
                        type="text"
                        value={shippingAddress.street}
                        onChange={(e) => { setShippingAddress({ ...shippingAddress, street: e.target.value }); setUseSavedAddress(false); setShippingCost(null); }}
                        placeholder={t('shop.street')}
                        className="input-brutal w-full text-sm py-2"
                        data-testid="shipping-street"
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={shippingAddress.postal_code}
                          onChange={(e) => { setShippingAddress({ ...shippingAddress, postal_code: e.target.value }); setUseSavedAddress(false); setShippingCost(null); }}
                          placeholder={t('shop.postalCode')}
                          className="input-brutal w-1/3 text-sm py-2"
                          data-testid="shipping-postal"
                        />
                        <input
                          type="text"
                          value={shippingAddress.city}
                          onChange={(e) => { setShippingAddress({ ...shippingAddress, city: e.target.value }); setUseSavedAddress(false); setShippingCost(null); }}
                          placeholder={t('shop.city')}
                          className="input-brutal flex-1 text-sm py-2"
                          data-testid="shipping-city"
                        />
                      </div>
                      <select
                        value={shippingAddress.country}
                        onChange={(e) => { setShippingAddress({ ...shippingAddress, country: e.target.value }); setUseSavedAddress(false); setShippingCost(null); }}
                        className="input-brutal w-full text-sm py-2"
                        data-testid="shipping-country"
                      >
                        <option value="France">France</option>
                        <option value="Belgique">Belgique</option>
                        <option value="Suisse">Suisse</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Canada">Canada</option>
                        <option value="Autre">Autre (International)</option>
                      </select>
                      <button
                        onClick={calculateShipping}
                        disabled={!addressValid}
                        className="btn-outline text-sm px-4 py-2 w-full disabled:opacity-40"
                        data-testid="calc-shipping-btn"
                      >
                        <Truck className="w-4 h-4 inline mr-1" /> Calculer les frais
                      </button>
                    </div>

                    {shippingCost && (
                      <div className="mt-3 p-3 bg-white rounded-lg border-2 border-charcoal/10">
                        <p className="text-sm text-charcoal">
                          <span className="font-bold">{shippingCost.shipping_method}</span>
                          <br />
                          <span className="text-charcoal/70">{shippingCost.delivery_days}</span>
                        </p>
                        <p className="text-lg font-bold text-charcoal mt-1">
                          {shippingCost.shipping_cost === 0 ? 'Gratuit!' : `${shippingCost.shipping_cost.toFixed(2)} EUR`}
                        </p>
                      </div>
                    )}

                    {!addressValid && shippingAddress.street.length > 0 && (
                      <p className="text-xs text-danger mt-2">Veuillez remplir tous les champs de l'adresse</p>
                    )}
                  </div>

                  <div className="border-t-2 border-charcoal pt-4">
                    {/* Promo code field */}
                    <div className="mb-4">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-charcoal/60 mb-1.5 block">Code promo</label>
                      {referralApplied ? (
                        <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-secondary/15 border-2 border-secondary">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Gift className="w-3.5 h-3.5 text-secondary shrink-0" strokeWidth={2.5} />
                              <code className="font-mono font-bold text-sm">{referralApplied.code}</code>
                              <span className="text-xs font-bold text-danger">-{referralApplied.discount_percent}%</span>
                            </div>
                            {referralApplied.influencer_name && (
                              <p className="text-[10px] text-charcoal/60 truncate">via {referralApplied.influencer_name}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={removeReferralCode}
                            className="text-charcoal/50 hover:text-danger p-1 shrink-0"
                            data-testid="remove-referral-btn"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={referralInput}
                            onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                            placeholder="INFLU2026"
                            className="input-brutal flex-1 text-sm font-mono"
                            data-testid="referral-input"
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyReferralCode())}
                          />
                          <button
                            type="button"
                            onClick={applyReferralCode}
                            disabled={referralChecking || !referralInput.trim()}
                            className="btn-outline text-sm px-3 py-2 disabled:opacity-50 whitespace-nowrap"
                            data-testid="apply-referral-btn"
                          >
                            {referralChecking ? '...' : 'Appliquer'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-charcoal/70">Sous-total</span>
                      <span className="font-bold text-charcoal">{cartTotal.toFixed(2)}€</span>
                    </div>
                    {referralApplied && (
                      <div className="flex justify-between items-center mb-2 text-danger">
                        <span className="text-sm">Reduction ({referralApplied.discount_percent}%)</span>
                        <span className="font-bold">-{(cartTotal * referralApplied.discount_percent / 100).toFixed(2)}€</span>
                      </div>
                    )}
                    {shippingCost && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-charcoal/70">Livraison</span>
                        <span className="font-bold text-charcoal">
                          {shippingCost.shipping_cost === 0 ? 'Gratuit' : `${shippingCost.shipping_cost.toFixed(2)}€`}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center mb-4 pt-2 border-t border-charcoal/10">
                      <span className="font-bold text-charcoal">Total</span>
                      <span className="text-xl font-bold text-charcoal font-heading">
                        {(cartTotal - (referralApplied ? cartTotal * referralApplied.discount_percent / 100 : 0) + (shippingCost?.shipping_cost || 0)).toFixed(2)}€
                      </span>
                    </div>

                    {cartTotal < 50 && (
                      <p className="text-sm text-charcoal/70 mb-4 text-center">
                        Plus que {(50 - cartTotal).toFixed(2)}€ pour la livraison gratuite!
                      </p>
                    )}

                    {user ? (
                      <button
                        onClick={handleCheckout}
                        disabled={checkingOut || !addressValid || !shippingCost}
                        data-testid="checkout-btn"
                        className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CreditCard className="w-5 h-5" strokeWidth={2.5} />
                        {checkingOut ? 'Redirection...' : !addressValid ? 'Adresse requise' : !shippingCost ? 'Calculez la livraison' : 'Payer avec Stripe'}
                      </button>
                    ) : (
                      <Link
                        to="/login"
                        data-testid="login-to-checkout"
                        className="w-full btn-primary flex items-center justify-center gap-2"
                        onClick={() => setShowCart(false)}
                      >
                        Connectez-vous pour commander
                      </Link>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/80 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setZoomedImage(null)}
          data-testid="image-zoom-modal"
        >
          <button
            onClick={() => setZoomedImage(null)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full border-2 border-charcoal shadow-brutal z-10"
          >
            <X className="w-6 h-6 text-charcoal" />
          </button>
          <img
            src={zoomedImage}
            alt="Zoom"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-2xl border-4 border-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Shop;
